import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Overlay>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Overlay
        ref={ref}
        className={cn(
            'fixed inset-0 z-50 bg-black/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            className,
        )}
        {...props}
    />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

/**
 * ---- ⛔⛔ A TALL DIALOG USED TO RUN OFF BOTH ENDS OF THE SCREEN ------------
 *
 * > Pete, 2026-09-09: *"When I click New Appointment frm the driver card, this
 * > screen pops but doesn't have a scroll bar - I can't see the buttons at the
 * > bottom"*
 *
 * ⛔ AND IT WAS NEVER A `NewAppointmentDialog` BUG. This shell was
 * `fixed top-1/2 -translate-y-1/2` with **no max-height and no overflow**, so a
 * dialog taller than the viewport grew equally past the top AND the bottom —
 * losing its title and its buttons at the same time — and because it is
 * `position: fixed`, THE PAGE CANNOT SCROLL TO REACH EITHER. There is no
 * gesture that recovers the Save button. The dialog is simply unusable.
 *
 * ⚠️ ALL 26 CALLERS SET A `max-w-`. NOT ONE SETS A `max-h-`. So every dialog in
 * this app carried the bug; New Appointment is only the one that grew tall
 * enough to prove it, on a laptop screen, months after the shell was written.
 *
 * ---- ⭐ WHY THE CAP LIVES HERE AND NOT IN THE CALLER -----------------------
 *
 * ⛔ FIXING THE ONE DIALOG PETE FOUND WOULD HAVE LEFT TWENTY-FIVE. And the next
 * one is found the same way: by somebody on a short screen who cannot submit a
 * form and has no idea why.
 *
 * ⭐ THE BLAST RADIUS IS EXACTLY THE BROKEN CASE. `max-h-[90vh]` changes
 * NOTHING for a dialog shorter than the viewport — no cap is reached, so no
 * scroller appears. It takes effect only on the dialogs that are already
 * unusable. ⚠️ And `cn` is `twMerge`, so a caller that later wants its own
 * height still wins by passing one.
 *
 * ---- ⛔ THE SCROLLER WRAPS `children`, NOT THE WHOLE CONTENT ---------------
 *
 * ⚠️ THE OBVIOUS ONE-LINER — `overflow-y-auto` on `Content` itself — puts the
 * close button INSIDE the scrolling box. `DialogPrimitive.Close` is
 * `absolute right-4 top-4`, so on a long dialog the X SCROLLS AWAY: the reader
 * is deep in a form with no visible way out, which is a worse version of the
 * bug being fixed. It stays a sibling of the scroller, pinned.
 *
 * ⛔⛔ AND THE WRAPPER MUST CARRY `grid gap-4`, WHICH MOVED OFF `Content`.
 * That class pair is what spaces every DialogHeader / body / DialogFooter in
 * the app. Left on `Content`, it would now apply to ONE child — the wrapper —
 * and every dialog's internal spacing would silently collapse to zero. ⚠️ That
 * is a change nothing would report: it compiles, it renders, and it just looks
 * wrong everywhere at once.
 *
 * ⚠️ `min-h-0` IS LOAD-BEARING. A flex child's default `min-height: auto`
 * refuses to shrink below its content, so without it the wrapper would ignore
 * the cap and overflow anyway — the classic silent no-op.
 *
 * ⭐ `p-6` STAYS ON `Content`, OUTSIDE THE SCROLLER, so scrolled content clips
 * at the padding edge instead of sliding under the border.
 */
const DialogContent = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
    <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
            ref={ref}
            className={cn(
                'fixed left-1/2 top-1/2 z-50 flex max-h-[90vh] w-full max-w-lg -translate-x-1/2 -translate-y-1/2 flex-col border bg-card p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:rounded-lg',
                className,
            )}
            {...props}
        >
            <div className="grid min-h-0 flex-1 gap-4 overflow-y-auto">
                {children}
            </div>
            <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
        </DialogPrimitive.Content>
    </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
    <div className={cn('flex flex-col space-y-1.5 text-left', className)} {...props} />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
    <div className={cn('flex flex-row justify-end space-x-2', className)} {...props} />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Title>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Title
        ref={ref}
        className={cn('text-lg font-semibold leading-none tracking-tight', className)}
        {...props}
    />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Description>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Description
        ref={ref}
        className={cn('text-sm text-muted-foreground', className)}
        {...props}
    />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
    Dialog,
    DialogPortal,
    DialogOverlay,
    DialogClose,
    DialogTrigger,
    DialogContent,
    DialogHeader,
    DialogFooter,
    DialogTitle,
    DialogDescription,
};
